import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDownload, FiLeaf, FiSun, FiCoffee } = FiIcons;

const Culinary = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const [activeTab, setActiveTab] = useState('breakfast');

  const menuTabs = [
    { id: 'breakfast', name: 'Breakfast', icon: FiSun },
    { id: 'lunch', name: 'Lunch & Dinner', icon: FiLeaf },
    { id: 'beverages', name: 'Beverages', icon: FiCoffee }
  ];

  const menuItems = {
    breakfast: [
      { name: 'Traditional Rice & Curry', description: 'Authentic village-style breakfast with coconut sambol', price: 'LKR 800' },
      { name: 'String Hoppers', description: 'Steamed rice noodles with curry and coconut milk', price: 'LKR 600' },
      { name: 'Fresh Fruit Platter', description: 'Seasonal tropical fruits from our garden', price: 'LKR 500' }
    ],
    lunch: [
      { name: 'Village Rice & Curry', description: 'Clay pot cooked with organic vegetables', price: 'LKR 1200' },
      { name: 'Grilled Fish', description: 'Fresh river fish with herbs and spices', price: 'LKR 1500' },
      { name: 'Vegetarian Feast', description: 'Farm-to-table organic vegetable curry', price: 'LKR 1000' }
    ],
    beverages: [
      { name: 'Fresh King Coconut', description: 'Welcome drink - refreshing and natural', price: 'LKR 200' },
      { name: 'Ceylon Tea', description: 'Premium highland tea from local estates', price: 'LKR 150' },
      { name: 'Fresh Juice', description: 'Seasonal fruit juices made fresh', price: 'LKR 300' }
    ]
  };

  const highlights = [
    {
      icon: FiLeaf,
      title: 'Organic Garden',
      description: 'Vegetables harvested fresh from our own organic garden',
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      icon: FiSun,
      title: 'Firewood Cooking',
      description: 'Traditional clay pot cooking over wood fires for authentic flavors',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }
  ];

  const handleDownloadMenu = () => {
    // In a real implementation, this would download a PDF menu
    const message = encodeURIComponent("Hi! Can you send me the full menu with current prices for Villa Springwater?");
    window.open(`https://wa.me/94704003478?text=${message}`, '_blank');
  };

  return (
    <section id="culinary" className="relative py-20 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <motion.div
          className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full blur-3xl"
          animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
          transition={{ duration: 20, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-r from-orange-500 to-yellow-400 rounded-full blur-3xl"
          animate={{ x: [0, -80, 0], y: [0, -60, 0] }}
          transition={{ duration: 15, repeat: Infinity, delay: 5 }}
        />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            A Culinary
            <span className="gradient-primary bg-clip-text text-transparent ml-3">
              Journey
            </span>
          </motion.h2>
          <motion.p
            className="text-xl text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Farm-to-table isn't a trend here; it's our way of life. Enjoy organic vegetables harvested from our own garden and cooked on traditional firewood stoves.
          </motion.p>
        </motion.div>

        {/* Highlights Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {highlights.map((highlight, index) => (
            <motion.div
              key={index}
              className="relative group overflow-hidden rounded-3xl"
              initial={{ opacity: 0, x: index === 0 ? -50 : 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 + index * 0.2 }}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={highlight.image}
                  alt={highlight.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              </div>

              {/* Content */}
              <div className="relative z-10 p-8 h-64 flex flex-col justify-end">
                <motion.div
                  className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 mb-4"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <SafeIcon icon={highlight.icon} className="text-white text-xl" />
                </motion.div>

                <h3 className="text-2xl font-bold text-white mb-3">
                  {highlight.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {highlight.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Menu Section */}
        <motion.div
          className="glass-morphism rounded-3xl p-8"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          {/* Menu Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {menuTabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'gradient-primary text-white shadow-lg'
                    : 'glass-dark text-gray-300 hover:text-white'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={tab.icon} />
                {tab.name}
              </motion.button>
            ))}
          </div>

          {/* Menu Items */}
          <motion.div
            key={activeTab}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {menuItems[activeTab].map((item, index) => (
              <motion.div
                key={index}
                className="glass-dark rounded-xl p-6 hover:bg-white/10 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <h4 className="text-lg font-bold text-white mb-2">{item.name}</h4>
                <p className="text-gray-300 text-sm mb-4 leading-relaxed">{item.description}</p>
                <div className="text-emerald-400 font-bold">{item.price}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Download Menu Button */}
          <motion.div
            className="text-center mt-8"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            <motion.button
              onClick={handleDownloadMenu}
              className="gradient-secondary text-white px-8 py-4 rounded-xl font-semibold flex items-center gap-3 mx-auto hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiDownload} />
              Get Full Menu via WhatsApp
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Bottom Quote */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.4 }}
        >
          <div className="glass-dark rounded-2xl p-6 max-w-2xl mx-auto">
            <p className="text-gray-300 text-lg italic">
              "Every meal is a celebration of the rainforest's bounty - from our garden to your table, 
              prepared with love and traditional wisdom passed down through generations."
            </p>
            <p className="text-emerald-400 font-semibold mt-4">- Villa Springwater Kitchen</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Culinary;